





import {
  defineComponent, onMounted, onUnmounted, ref,
} from '@nuxtjs/composition-api';

export default defineComponent({
  name: 'LoadWhenVisible',
  props: {
    options: {
      type: Object,
      default: () => ({
        rootMargin: '0px 0px 200px 0px',
      }),
    },
    // If true, reveal content once the user scrolls at least `scrollThreshold` px
    enableScrollTrigger: {
      type: Boolean,
      default: true,
    },
    // If true, reveal content on first user interaction (mousemove/keydown/touch/wheel)
    enableInteractionTrigger: {
      type: Boolean,
      default: true,
    },
    // Minimum vertical scroll (in px) required to reveal content
    scrollThreshold: {
      type: Number,
      default: 2,
    },

  },
  setup({ options, enableScrollTrigger, enableInteractionTrigger, scrollThreshold }) {
    const isVisible = ref(false);
    const root = ref(null);

    let observer = null;
    if (!enableScrollTrigger) {
      if (process.browser && ('IntersectionObserver' in window)) {
        observer = new IntersectionObserver((entries) => {
          if (entries[0].intersectionRatio <= 0) return;
          observer.unobserve(root.value);
          isVisible.value = true;
        }, options);
      }
    }

    let onScroll;
    let onInteract;
    let initialScrollY = 0;
    onMounted(() => {
      if (process.client && enableScrollTrigger) {
        initialScrollY = window.scrollY || 0;
        const checkScroll = () => {
          const current = window.scrollY || 0;
          if ((current - initialScrollY) >= scrollThreshold) {
            isVisible.value = true;
            window.removeEventListener('scroll', onScroll, { capture: false } as any);
            if (observer && root.value) observer.unobserve(root.value);
          }
        };
        onScroll = () => checkScroll();
        // Do not auto-show even if page loads scrolled; wait for a real scroll event
        window.addEventListener('scroll', onScroll, { passive: true });
      }

      if (process.client && enableInteractionTrigger) {
        const reveal = () => {
          isVisible.value = true;
          detachInteractionListeners();
          if (observer && root.value) observer.unobserve(root.value);
          if (onScroll) window.removeEventListener('scroll', onScroll as any, { capture: false } as any);
        };
        onInteract = () => reveal();
        const detachInteractionListeners = () => {
          window.removeEventListener('mousemove', onInteract as any, { capture: false } as any);
          window.removeEventListener('keydown', onInteract as any, { capture: false } as any);
          window.removeEventListener('touchstart', onInteract as any, { capture: false } as any);
          window.removeEventListener('pointerdown', onInteract as any, { capture: false } as any);
          window.removeEventListener('wheel', onInteract as any, { capture: false } as any);
        };
        // Attach once; reveal on first user interaction
        window.addEventListener('mousemove', onInteract as any, { passive: true });
        window.addEventListener('keydown', onInteract as any, { passive: true } as any);
        window.addEventListener('touchstart', onInteract as any, { passive: true });
        window.addEventListener('pointerdown', onInteract as any, { passive: true } as any);
        window.addEventListener('wheel', onInteract as any, { passive: true });
        // In case already visible by other trigger, clean up immediately
        if (isVisible.value) detachInteractionListeners();
      }
      if (observer) {
        // Only observe if not already visible from scroll check
        if (!isVisible.value) observer.observe(root.value);
      }
    });

    onUnmounted(() => {
      if (observer) {
        observer.unobserve(root.value);
      }
      if (process.client && enableScrollTrigger && onScroll) {
        window.removeEventListener('scroll', onScroll as any, { capture: false } as any);
      }
      if (process.client && enableInteractionTrigger && onInteract) {
        window.removeEventListener('mousemove', onInteract as any, { capture: false } as any);
        window.removeEventListener('keydown', onInteract as any, { capture: false } as any);
        window.removeEventListener('touchstart', onInteract as any, { capture: false } as any);
        window.removeEventListener('pointerdown', onInteract as any, { capture: false } as any);
        window.removeEventListener('wheel', onInteract as any, { capture: false } as any);
      }
    });

    return {
      root,
      isVisible,
    };
  },
});
